import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Calculator, Euro, Leaf, ArrowRight, Info, ChevronDown, ChevronUp, Award, Zap, 
  Clock, TrendingUp, ExternalLink, X, FileText, Calendar, Percent, Building2,
  CheckCircle, AlertCircle, DollarSign, Shield, Home, ChevronLeft, ChevronRight
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import LoginPromptModal from "./LoginPromptModal";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bank logos mapping
const BANK_LOGOS = {
  "BCR": "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8c/BCR_logo.svg/200px-BCR_logo.svg.png",
  "BRD": "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d8/BRD_-_Groupe_Soci%C3%A9t%C3%A9_G%C3%A9n%C3%A9rale_logo.svg/200px-BRD_-_Groupe_Soci%C3%A9t%C3%A9_G%C3%A9n%C3%A9rale_logo.svg.png",
  "Banca Transilvania": "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Banca_Transilvania_logo.svg/200px-Banca_Transilvania_logo.svg.png",
  "UniCredit Bank": "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e1/UniCredit_logo.svg/200px-UniCredit_logo.svg.png",
  "Raiffeisen Bank": "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1a/Raiffeisen_Bank_logo.svg/200px-Raiffeisen_Bank_logo.svg.png",
  "ING Bank": "https://upload.wikimedia.org/wikipedia/commons/thumb/4/49/ING_Group_N.V._Logo.svg/200px-ING_Group_N.V._Logo.svg.png",
  "Garanti BBVA": "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8c/Garanti_logo.svg/200px-Garanti_logo.svg.png",
  "Exim Bank": "https://www.eximbank.ro/wp-content/uploads/2021/06/logo-eximbank.png",
};

const BANK_COLORS = {
  "BCR": "#003366",
  "BRD": "#DC0032",
  "Banca Transilvania": "#FFD100",
  "UniCredit Bank": "#E40613",
  "Raiffeisen Bank": "#FFE600",
  "ING Bank": "#FF6200",
  "Garanti BBVA": "#00A650",
  "Exim Bank": "#1E3A5F",
};

const CreditSimulatorAdvanced = ({ propertyPrice, listingId, energyCertificate = "", propertyType = "apartament" }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  // Basic inputs
  const [downPayment, setDownPayment] = useState(Math.round(propertyPrice * 0.15));
  const [loanTermYears, setLoanTermYears] = useState(25);
  const [selectedCertificate, setSelectedCertificate] = useState(energyCertificate || "B");
  
  // Filter state
  const [sortBy, setSortBy] = useState("rata_fixa"); // rata_fixa, rata_variabila, costuri
  const [rateType, setRateType] = useState("fix3ani");
  
  // Data state
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ircc, setIrcc] = useState(null);
  const [allOffers, setAllOffers] = useState([]);
  const [exchangeRate, setExchangeRate] = useState(4.98); // EUR/RON rate
  
  // Modal state
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleData, setScheduleData] = useState([]);
  const [schedulePage, setSchedulePage] = useState(0);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const minDownPayment = Math.round(propertyPrice * 0.15);
  const maxDownPayment = Math.round(propertyPrice * 0.80);
  const loanAmount = propertyPrice - downPayment;
  const loanAmountRON = loanAmount * exchangeRate;

  useEffect(() => {
    if (energyCertificate) {
      setSelectedCertificate(energyCertificate);
    }
  }, [energyCertificate]);

  useEffect(() => {
    fetchIRCC();
    fetchExchangeRate();
  }, []);

  const fetchExchangeRate = async () => {
    try {
      const response = await axios.get(`${API}/credit/exchange-rate`);
      setExchangeRate(response.data.EUR_RON);
    } catch (error) {
      console.error("Error fetching exchange rate:", error);
    }
  };

  useEffect(() => {
    if (selectedCertificate) {
      fetchAllOffers();
    }
  }, [downPayment, loanTermYears, selectedCertificate, rateType]);

  useEffect(() => {
    if (allOffers.length > 0) {
      sortOffers();
    }
  }, [sortBy, allOffers]);

  const fetchIRCC = async () => {
    try {
      const response = await axios.get(`${API}/credit/ircc`);
      setIrcc(response.data);
    } catch (error) {
      console.error("Error fetching IRCC:", error);
    }
  };

  const fetchAllOffers = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/credit/simulate/full`, {
        property_price: propertyPrice,
        down_payment: downPayment,
        loan_term_years: loanTermYears,
        energy_certificate: selectedCertificate,
        rate_type: rateType,
        property_type: propertyType
      });
      setAllOffers(response.data.offers || []);
      setResult(response.data);
    } catch (error) {
      console.error("Error fetching offers:", error);
      // Fallback to regular endpoint
      try {
        const fallback = await axios.post(`${API}/credit/simulate`, {
          property_price: propertyPrice,
          down_payment: downPayment,
          loan_term_years: loanTermYears,
          energy_certificate: selectedCertificate,
          num_offers: 10,
          rate_type: rateType
        });
        setAllOffers(fallback.data.offers || []);
        setResult(fallback.data);
      } catch (e) {
        console.error("Fallback also failed:", e);
      }
    } finally {
      setLoading(false);
    }
  };

  const sortOffers = () => {
    let sorted = [...allOffers];
    switch (sortBy) {
      case "rata_fixa":
        sorted.sort((a, b) => a.monthly_payment - b.monthly_payment);
        break;
      case "rata_variabila":
        sorted.sort((a, b) => (a.variable_rate || a.interest_rate) - (b.variable_rate || b.interest_rate));
        break;
      case "costuri":
        sorted.sort((a, b) => (a.total_costs || 0) - (b.total_costs || 0));
        break;
      default:
        sorted.sort((a, b) => a.monthly_payment - b.monthly_payment);
    }
    setResult(prev => ({...prev, offers: sorted, best_offer: sorted[0]}));
  };

  const generateSchedule = (offer) => {
    const schedule = [];
    let balance = loanAmount;
    const monthlyRate = offer.interest_rate / 100 / 12;
    const numPayments = loanTermYears * 12;
    const monthlyPayment = offer.monthly_payment;
    
    for (let month = 1; month <= numPayments; month++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = monthlyPayment - interestPayment;
      balance = Math.max(0, balance - principalPayment);
      
      schedule.push({
        month,
        year: Math.ceil(month / 12),
        payment: monthlyPayment,
        principal: principalPayment,
        interest: interestPayment,
        balance: balance
      });
    }
    return schedule;
  };

  const openOfferDetails = (offer) => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }
    setSelectedOffer(offer);
    const schedule = generateSchedule(offer);
    setScheduleData(schedule);
    setSchedulePage(0);
  };

  const handleApply = (offer) => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }
    navigate(`/aplica-credit/${listingId}`, { 
      state: { 
        selectedBank: offer?.bank_name,
        interestRate: offer?.interest_rate,
        monthlyPayment: offer?.monthly_payment
      }
    });
  };

  const getCertificateColor = (cert) => {
    const colors = {
      A: "bg-green-500 text-white",
      B: "bg-green-400 text-white",
      C: "bg-yellow-400 text-black",
      D: "bg-yellow-500 text-black",
      E: "bg-orange-400 text-white",
      F: "bg-orange-500 text-white",
      G: "bg-red-500 text-white"
    };
    return colors[cert] || "bg-stone-200";
  };

  const formatCurrency = (value, currency = "EUR") => {
    // Creditele sunt acordate în RON, deci afișăm ratele lunare în lei
    return `${value.toLocaleString('ro-RO')} lei`;
  };

  const downPaymentPercent = Math.round((downPayment / propertyPrice) * 100);
  const isGreenCertificate = selectedCertificate?.toUpperCase() === "A";
  const itemsPerPage = 12;

  return (
    <div data-testid="credit-simulator-advanced" className="space-y-6">
      {/* Header Card - Compact */}
      <div className="bg-gradient-to-r from-emerald-950 to-emerald-800 rounded-xl p-4 text-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Calculator className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-['Outfit'] font-bold text-base">Simulator Credit Ipotecar</h3>
              <p className="text-emerald-200 text-xs">Date de la Brokerul.ro</p>
            </div>
          </div>
          {ircc && (
            <div className="text-right bg-white/10 px-3 py-1 rounded-lg">
              <p className="text-emerald-200 text-xs">IRCC</p>
              <p className="text-lg font-bold">{ircc.valoare}%</p>
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-white/10 rounded-lg p-2">
            <p className="text-emerald-200 text-[10px]">Proprietate</p>
            <p className="text-sm font-bold">{formatCurrency(propertyPrice)}</p>
          </div>
          <div className="bg-white/10 rounded-lg p-2">
            <p className="text-emerald-200 text-[10px]">Avans ({downPaymentPercent}%)</p>
            <p className="text-sm font-bold">{formatCurrency(downPayment)}</p>
          </div>
          <div className="bg-white/10 rounded-lg p-2">
            <p className="text-emerald-200 text-[10px]">Credit</p>
            <p className="text-sm font-bold">{formatCurrency(loanAmount)}</p>
          </div>
        </div>
      </div>

      {/* Filters Card - Compact */}
      <div className="bg-white rounded-xl p-4 border border-stone-200">
        <div className="grid grid-cols-2 gap-3">
          {/* Energy Certificate */}
          <div>
            <Label className="text-stone-600 mb-1 flex items-center gap-1 text-xs">
              <Zap className="w-3 h-3 text-amber-500" />
              Certificat Energetic *
            </Label>
            <Select value={selectedCertificate} onValueChange={setSelectedCertificate}>
              <SelectTrigger data-testid="certificate-select" className="h-9 text-sm">
                <SelectValue placeholder="Selectează" />
              </SelectTrigger>
              <SelectContent>
                {["A", "B", "C", "D", "E", "F", "G"].map((cert) => (
                  <SelectItem key={cert} value={cert}>
                    <div className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded flex items-center justify-center text-xs font-bold ${getCertificateColor(cert)}`}>
                        {cert}
                      </span>
                      Clasa {cert}
                      {cert === "A" && (
                        <Badge className="bg-green-100 text-green-700 text-[10px] ml-1">Reducere</Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Rate Type */}
          <div>
            <Label className="text-stone-600 mb-1 flex items-center gap-1 text-xs">
              <Clock className="w-3 h-3 text-blue-500" />
              Tip dobândă
            </Label>
            <Select value={rateType} onValueChange={setRateType}>
              <SelectTrigger data-testid="rate-type-select" className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fix2ani">Fix 2 ani</SelectItem>
                <SelectItem value="fix3ani">Fix 3 ani ⭐</SelectItem>
                <SelectItem value="fix5ani">Fix 5 ani</SelectItem>
                <SelectItem value="variabila">Variabilă (IRCC)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Loan Term */}
          <div>
            <Label className="text-stone-600 mb-1 flex items-center gap-1 text-xs">
              <Calendar className="w-3 h-3 text-purple-500" />
              Perioadă
            </Label>
            <Select value={loanTermYears.toString()} onValueChange={(v) => setLoanTermYears(parseInt(v))}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 15, 20, 25, 30].map(y => (
                  <SelectItem key={y} value={y.toString()}>{y} ani</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sort By */}
          <div>
            <Label className="text-stone-600 mb-1 flex items-center gap-1 text-xs">
              <TrendingUp className="w-3 h-3 text-emerald-500" />
              Sortare
            </Label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger data-testid="sort-select" className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rata_fixa">Cea mai mică rată</SelectItem>
                <SelectItem value="rata_variabila">Rată variabilă</SelectItem>
                <SelectItem value="costuri">Cele mai mici costuri</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Down Payment Input */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-stone-600 text-sm">Avans ({downPaymentPercent}%)</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger><Info className="w-4 h-4 text-stone-400" /></TooltipTrigger>
                <TooltipContent><p>Minim 15% din valoarea proprietății. Creditul se acordă în RON.</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          
          {/* Quick percent buttons */}
          <div className="flex gap-2 mb-2">
            <Button
              variant={downPaymentPercent === 15 ? "default" : "outline"}
              size="sm"
              className={`flex-1 h-8 text-xs ${downPaymentPercent === 15 ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
              onClick={() => setDownPayment(Math.round(propertyPrice * 0.15))}
            >
              15% (minim)
            </Button>
            <Button
              variant={downPaymentPercent === 25 ? "default" : "outline"}
              size="sm"
              className={`flex-1 h-8 text-xs ${downPaymentPercent === 25 ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
              onClick={() => setDownPayment(Math.round(propertyPrice * 0.25))}
            >
              25%
            </Button>
            <Button
              variant={downPaymentPercent >= 30 && downPaymentPercent < 50 ? "default" : "outline"}
              size="sm"
              className={`flex-1 h-8 text-xs ${downPaymentPercent >= 30 && downPaymentPercent < 50 ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
              onClick={() => setDownPayment(Math.round(propertyPrice * 0.35))}
            >
              35%
            </Button>
          </div>
          
          <div className="flex items-center gap-2">
            <Input
              data-testid="down-payment-input"
              type="number"
              value={downPayment}
              onChange={(e) => {
                const val = Math.max(minDownPayment, Math.min(maxDownPayment, Number(e.target.value) || minDownPayment));
                setDownPayment(val);
              }}
              min={minDownPayment}
              max={maxDownPayment}
              step={1000}
              className="flex-1"
            />
            <span className="text-stone-500 text-sm whitespace-nowrap">EUR</span>
          </div>
          <div className="flex justify-between text-xs text-stone-400 mt-1">
            <span>Min: {formatCurrency(minDownPayment)} (15%)</span>
            <span>Max: {formatCurrency(maxDownPayment)} (80%)</span>
          </div>
          {/* RON conversion info */}
          <div className="mt-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-xs text-amber-700">
              <strong>Credit în RON:</strong> {(loanAmountRON).toLocaleString('ro-RO', { maximumFractionDigits: 0 })} RON
              <span className="text-amber-500 ml-1">(curs BNR: {exchangeRate.toFixed(4)})</span>
            </p>
          </div>
        </div>
      </div>

      {/* Offers Section - Compact */}
      <div className="bg-white rounded-xl p-4 border border-stone-200">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-['Outfit'] font-bold text-base text-stone-900">Oferte de credit</h4>
          {result?.offers && (
            <Badge variant="secondary" className="text-sm">
              {result.offers.length} bănci • {rateType === "variabila" ? "Variabilă" : `Fix ${rateType.replace("fix", "").replace("ani", " ani")}`}
            </Badge>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-stone-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : result?.offers && result.offers.length > 0 ? (
          <div className="space-y-3">
            {/* Best Offer - Highlighted */}
            {result.best_offer && (
              <div 
                onClick={() => openOfferDetails(result.best_offer)}
                className="relative bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-300 rounded-xl p-4 cursor-pointer hover:shadow-lg transition-all"
              >
                <div className="absolute -top-2 left-3">
                  <Badge className="bg-emerald-600 text-white px-2 py-0.5 text-xs">
                    <Award className="w-3 h-3 mr-1" />
                    Cea mai bună
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between mt-1">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center p-1"
                      style={{ backgroundColor: BANK_COLORS[result.best_offer.bank_name] + '20' }}
                    >
                      <Building2 className="w-6 h-6" style={{ color: BANK_COLORS[result.best_offer.bank_name] }} />
                    </div>
                    <div>
                      <h5 className="font-bold text-sm text-stone-900">{result.best_offer.bank_name}</h5>
                      <p className="text-stone-600 text-xs">
                        Dobândă: <span className="font-semibold">{result.best_offer.interest_rate}%</span>
                        {isGreenCertificate && (
                          <span className="text-green-600 ml-1">(clasa {selectedCertificate})</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-emerald-700">
                      {formatCurrency(result.best_offer.monthly_payment)}
                    </p>
                    <p className="text-stone-500 text-xs">/ lună</p>
                  </div>
                </div>
                
                <div className="absolute bottom-1 right-3 text-emerald-600 text-xs flex items-center gap-1">
                  Detalii <ChevronRight className="w-3 h-3" />
                </div>
              </div>
            )}

            {/* All Offers - Complete List */}
            <div className="space-y-2">
              {result.offers.slice(1).map((offer, idx) => (
                <div
                  key={idx}
                  onClick={() => openOfferDetails(offer)}
                  className="bg-stone-50 hover:bg-white border border-stone-200 hover:border-stone-300 rounded-lg p-3 cursor-pointer transition-all hover:shadow-sm flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-8 h-8 rounded-md flex items-center justify-center"
                      style={{ backgroundColor: BANK_COLORS[offer.bank_name] + '20' }}
                    >
                      <Building2 className="w-5 h-5" style={{ color: BANK_COLORS[offer.bank_name] }} />
                    </div>
                    <div>
                      <h5 className="font-semibold text-stone-900 text-sm">{offer.bank_name}</h5>
                      <p className="text-stone-500 text-xs">{offer.interest_rate}% dobândă</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-stone-900">{formatCurrency(offer.monthly_payment)}</p>
                    <p className="text-stone-400 text-xs">/ lună</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-stone-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            <p>Nu există oferte disponibile pentru aceste criterii.</p>
            <p className="text-sm">Încearcă să modifici filtrele.</p>
          </div>
        )}

        {/* Apply Button */}
        <div className="mt-4 pt-4 border-t border-stone-200">
          <Button
            onClick={() => handleApply(result?.best_offer)}
            disabled={!result?.best_offer}
            className="w-full h-10 bg-amber-500 hover:bg-amber-600 rounded-lg font-semibold"
          >
            Aplică pentru credit
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          
          <div className="flex items-center justify-center mt-3">
            <a 
              href="https://brokerul.ro/comparator-credite.html?tip=ipotecar" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-stone-400 hover:text-stone-600 text-xs flex items-center gap-1"
            >
              Comparator complet pe Brokerul.ro <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>

      {/* Offer Details Modal */}
      <Dialog open={!!selectedOffer} onOpenChange={() => setSelectedOffer(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedOffer && (
            <div className="flex flex-col">
              <DialogHeader className="flex-shrink-0">
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center p-2"
                    style={{ backgroundColor: BANK_COLORS[selectedOffer.bank_name] + '20' }}
                  >
                    <Building2 className="w-6 h-6" style={{ color: BANK_COLORS[selectedOffer.bank_name] }} />
                  </div>
                  <div>
                    <DialogTitle className="text-lg">{selectedOffer.bank_name}</DialogTitle>
                    <DialogDescription className="text-sm">
                      Credit ipotecar • {rateType === "variabila" ? "Dobândă variabilă" : `Dobândă fixă ${rateType.replace("fix", "").replace("ani", " ani")}`}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <Tabs defaultValue="detalii" className="mt-3 flex-1">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="detalii">Detalii ofertă</TabsTrigger>
                  <TabsTrigger value="costuri">Costuri</TabsTrigger>
                  <TabsTrigger value="scadentar">Scadențar</TabsTrigger>
                </TabsList>

                <TabsContent value="detalii" className="mt-3">
                  <div className="grid grid-cols-2 gap-3">
                    {/* Main Info */}
                    <div className="bg-emerald-50 rounded-lg p-3">
                      <p className="text-emerald-600 text-xs font-medium mb-0.5">Rată lunară</p>
                      <p className="text-2xl font-bold text-emerald-700">{formatCurrency(selectedOffer.monthly_payment)}</p>
                    </div>
                    <div className="bg-stone-100 rounded-lg p-3">
                      <p className="text-stone-500 text-xs font-medium mb-0.5">Dobândă</p>
                      <p className="text-2xl font-bold text-stone-900">{selectedOffer.interest_rate}%</p>
                    </div>
                    <div className="bg-stone-100 rounded-lg p-3">
                      <p className="text-stone-500 text-xs font-medium mb-0.5">Credit solicitat</p>
                      <p className="text-lg font-bold text-stone-900">{formatCurrency(loanAmount)}</p>
                    </div>
                    <div className="bg-stone-100 rounded-lg p-3">
                      <p className="text-stone-500 text-xs font-medium mb-0.5">Total de plată</p>
                      <p className="text-lg font-bold text-stone-900">{formatCurrency(selectedOffer.total_payment)}</p>
                    </div>
                    <div className="bg-stone-100 rounded-lg p-3">
                      <p className="text-stone-500 text-xs font-medium mb-0.5">Dobândă totală</p>
                      <p className="text-lg font-bold text-amber-600">{formatCurrency(selectedOffer.total_interest)}</p>
                    </div>
                    <div className="bg-stone-100 rounded-lg p-3">
                      <p className="text-stone-500 text-xs font-medium mb-0.5">Perioadă</p>
                      <p className="text-lg font-bold text-stone-900">{loanTermYears} ani ({loanTermYears * 12} rate)</p>
                    </div>
                  </div>

                  {/* Conditions */}
                  {selectedOffer.special_conditions && selectedOffer.special_conditions.length > 0 && (
                    <div className="mt-4">
                      <h5 className="font-semibold text-stone-900 mb-2 text-sm">Condiții și beneficii</h5>
                      <div className="space-y-1.5">
                        {selectedOffer.special_conditions.map((cond, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                            <span className="text-stone-700 text-sm">{cond}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="costuri" className="mt-3">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2.5 bg-stone-50 rounded-lg">
                      <span className="text-stone-600 text-sm">Comision acordare</span>
                      <span className="font-semibold text-sm">{selectedOffer.comision ? formatCurrency(selectedOffer.comision, "RON") : "Variabil"}</span>
                    </div>
                    <div className="flex items-center justify-between p-2.5 bg-stone-50 rounded-lg">
                      <span className="text-stone-600 text-sm">Comision administrare lunar</span>
                      <span className="font-semibold text-sm">{selectedOffer.comisionAdministrare ? formatCurrency(selectedOffer.comisionAdministrare, "RON") : "0 lei"}</span>
                    </div>
                    <div className="flex items-center justify-between p-2.5 bg-stone-50 rounded-lg">
                      <span className="text-stone-600 text-sm">Evaluare apartament</span>
                      <span className="font-semibold text-sm">{selectedOffer.evaluareAp ? formatCurrency(selectedOffer.evaluareAp, "RON") : "Variabil"}</span>
                    </div>
                    <div className="flex items-center justify-between p-2.5 bg-stone-50 rounded-lg">
                      <span className="text-stone-600 text-sm">Evaluare casă</span>
                      <span className="font-semibold text-sm">{selectedOffer.evaluareCasa ? formatCurrency(selectedOffer.evaluareCasa, "RON") : "Variabil"}</span>
                    </div>
                    <div className="flex items-center justify-between p-2.5 bg-stone-50 rounded-lg">
                      <span className="text-stone-600 text-sm">Asigurare imobil (anual)</span>
                      <span className="font-semibold text-sm">{selectedOffer.asigurare ? `${(selectedOffer.asigurare * 100).toFixed(2)}% din sold` : "Variabil"}</span>
                    </div>
                    <div className="flex items-center justify-between p-2.5 bg-stone-50 rounded-lg">
                      <span className="text-stone-600 text-sm">PAD (Polița Anti-Dezastre)</span>
                      <span className="font-semibold text-sm">{selectedOffer.pad ? formatCurrency(selectedOffer.pad, "RON") : "~130 lei"}</span>
                    </div>
                    <div className="flex items-center justify-between p-2.5 bg-stone-50 rounded-lg">
                      <span className="text-stone-600 text-sm">Comision rambursare anticipată</span>
                      <span className="font-semibold text-sm text-emerald-600">{selectedOffer.comisionRambursare || "0%"}</span>
                    </div>
                    <div className="flex items-center justify-between p-2.5 bg-amber-50 rounded-lg">
                      <span className="text-amber-700 font-medium text-sm">Valabilitate ofertă</span>
                      <span className="font-semibold text-sm text-amber-700">{selectedOffer.valabilitate || "90 zile"}</span>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="scadentar" className="mt-3">
                  <ScrollArea className="h-[250px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-14 text-xs">Luna</TableHead>
                          <TableHead className="text-xs">Rată</TableHead>
                          <TableHead className="text-xs">Principal</TableHead>
                          <TableHead className="text-xs">Dobândă</TableHead>
                          <TableHead className="text-xs">Sold rămas</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {scheduleData.slice(schedulePage * itemsPerPage, (schedulePage + 1) * itemsPerPage).map((row) => (
                          <TableRow key={row.month}>
                            <TableCell className="font-medium text-sm py-2">{row.month}</TableCell>
                            <TableCell className="text-sm py-2">{formatCurrency(row.payment)}</TableCell>
                            <TableCell className="text-emerald-600 text-sm py-2">{formatCurrency(row.principal)}</TableCell>
                            <TableCell className="text-amber-600 text-sm py-2">{formatCurrency(row.interest)}</TableCell>
                            <TableCell className="text-sm py-2">{formatCurrency(row.balance)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                  
                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={schedulePage === 0}
                      onClick={() => setSchedulePage(p => p - 1)}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                    </Button>
                    <span className="text-xs text-stone-500">
                      Pagina {schedulePage + 1} din {Math.ceil(scheduleData.length / itemsPerPage)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={(schedulePage + 1) * itemsPerPage >= scheduleData.length}
                      onClick={() => setSchedulePage(p => p + 1)}
                    >
                      Următor <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>

                  {/* Summary */}
                  <div className="mt-3 p-3 bg-stone-100 rounded-lg">
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-stone-500 text-xs">Total rate</p>
                        <p className="font-bold">{loanTermYears * 12}</p>
                      </div>
                      <div>
                        <p className="text-stone-500 text-xs">Total plătit</p>
                        <p className="font-bold">{formatCurrency(selectedOffer.total_payment)}</p>
                      </div>
                      <div>
                        <p className="text-stone-500 text-xs">Total dobândă</p>
                        <p className="font-bold text-amber-600">{formatCurrency(selectedOffer.total_interest)}</p>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Apply Button in Modal */}
              <div className="mt-4 flex gap-2 flex-shrink-0">
                <Button
                  onClick={() => handleApply(selectedOffer)}
                  className="flex-1 h-10 bg-amber-500 hover:bg-amber-600"
                >
                  Aplică la {selectedOffer.bank_name}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setSelectedOffer(null)}
                  className="h-10"
                >
                  Închide
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Login Prompt for Credit Details */}
      <LoginPromptModal
        open={showLoginPrompt}
        onOpenChange={setShowLoginPrompt}
        variant="credit"
        returnUrl={`/proprietate/${listingId}`}
      />
    </div>
  );
};

export default CreditSimulatorAdvanced;
